import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Space, Breadcrumb, Card, Result, Button } from "antd";
import { PageHeader } from "@ant-design/pro-layout";

import type { Tenant } from "@chirpstack/chirpstack-api-grpc-web/api/tenant_pb";
import type { CreateApplicationResponse, ListApplicationsResponse } from "@chirpstack/chirpstack-api-grpc-web/api/application_pb";
import { Application, CreateApplicationRequest, ListApplicationsRequest } from "@chirpstack/chirpstack-api-grpc-web/api/application_pb";

import ApplicationForm from "./ApplicationForm";
import ApplicationStore from "../../stores/ApplicationStore";
import { useTitle } from "../helpers";
import { usePlanGates } from "../../contexts/PlanFeaturesContext";

interface IProps {
  tenant: Tenant;
}

function CreateApplication(props: IProps) {
  const navigate = useNavigate();
  useTitle("Tenants", props.tenant.getName(), "Networks", "Add");

  // Guard direct navigation to this route — the list page disables the
  // Add button at limit, but nothing stops a bookmarked URL. We block
  // the form render and point the user back at the list with an
  // explanation rather than letting them fill out a form that the
  // Phase 3 Rust layer will reject anyway.
  const { features, globalAdmin, atApplicationsLimit } = usePlanGates();
  const [applicationCount, setApplicationCount] = useState<number | null>(null);

  useEffect(() => {
    const req = new ListApplicationsRequest();
    req.setTenantId(props.tenant.getId());
    req.setLimit(1);
    req.setOffset(0);
    ApplicationStore.list(req, (resp: ListApplicationsResponse) => {
      setApplicationCount(resp.toObject().totalCount);
    });
  }, [props.tenant]);

  // Hold render while the count fetch is in flight for gated tenants —
  // otherwise there's a brief window between mount and the gRPC response
  // where the form renders submittable and a fast submit bypasses the
  // cap. Admins and pre-SM tenants (hasPlanTags=false) are never gated,
  // so they skip the wait entirely.
  const gateReady = applicationCount !== null;
  const gateLoading =
    !gateReady && !globalAdmin && features.hasPlanTags && features.maxApplications > 0;
  const atLimit = gateReady && atApplicationsLimit(applicationCount!);

  const onFinish = (obj: Application) => {
    obj.setTenantId(props.tenant.getId());

    const req = new CreateApplicationRequest();
    req.setApplication(obj);

    ApplicationStore.create(req, (resp: CreateApplicationResponse) => {
      navigate(`/tenants/${props.tenant.getId()}/applications/${resp.getId()}`);
    });
  };

  const app = new Application();

  // Withhold render entirely while the gate is still resolving. Keeps the
  // user from seeing either a flash of the form or a flash of the "limit
  // reached" warning during the fetch window.
  if (gateLoading) {
    return null;
  }

  if (atLimit) {
    const limitMsg = `Your plan includes up to ${features.maxApplications} network${features.maxApplications === 1 ? "" : "s"}.`;
    return (
      <Result
        status="warning"
        title="Network limit reached"
        subTitle={`${limitMsg} Remove an existing network or upgrade your plan to add more.`}
        extra={
          <Button type="primary" onClick={() => navigate(`/tenants/${props.tenant.getId()}/applications`)}>
            Back to networks
          </Button>
        }
      />
    );
  }

  return (
    <Space orientation="vertical" style={{ width: "100%" }} size="large">
      <PageHeader
        breadcrumbRender={() => (
          <Breadcrumb
            items={[
              { title: "Tenants" },
              { title: <Link to={`/tenants/${props.tenant.getId()}`}>{props.tenant.getName()}</Link> },
              { title: <Link to={`/tenants/${props.tenant.getId()}/applications`}>Networks</Link> },
              { title: "Add" },
            ]}
          />
        )}
        title="Add network"
      />
      <Card>
        <ApplicationForm initialValues={app} onFinish={onFinish} />
      </Card>
    </Space>
  );
}

export default CreateApplication;
